import { useCallback, useEffect, useRef } from "react";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";

interface UseOrgIntegrationBulkCommandActionsOptions {
    selectedCount: number;
    eligibleAccountCount: number;
    /** Selected accounts still waiting for a client. */
    eligibleClientCount: number;
    /** Selected accounts whose entity can be set or changed. */
    eligibleEntityCount: number;
    onSendEmail: () => void;
    onClearSelection: () => void;
    /** Left undefined while the action is unavailable, which hides it. */
    onUpdateClient?: () => void;
    onUpdateEntity?: () => void;
}

/**
 * Puts the integrations bulk actions behind ⌘K while rows are selected.
 *
 * `registerOpenOverride` is a single slot with no stacking, so the override is
 * registered only while a selection exists — otherwise this page would keep
 * hijacking ⌘K after the user cleared their rows.
 */
export function useOrgIntegrationBulkCommandActions({
    selectedCount,
    eligibleAccountCount,
    eligibleClientCount,
    eligibleEntityCount,
    onSendEmail,
    onClearSelection,
    onUpdateClient,
    onUpdateEntity,
}: UseOrgIntegrationBulkCommandActionsOptions) {
    const {
        open: openCommands,
        close,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const handleSendEmail = useCallback(() => {
        close();
        onSendEmail();
    }, [close, onSendEmail]);

    const handleClearSelection = useCallback(() => {
        close();
        onClearSelection();
    }, [close, onClearSelection]);

    const handleUpdateClient = useCallback(() => {
        close();
        onUpdateClient?.();
    }, [close, onUpdateClient]);

    const handleUpdateEntity = useCallback(() => {
        close();
        onUpdateEntity?.();
    }, [close, onUpdateEntity]);

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.ORG_INTEGRATION_BULK, {
            onOrgIntegrationBulkSendEmail: handleSendEmail,
            onOrgIntegrationBulkClearSelection: handleClearSelection,
            onOrgIntegrationBulkUpdateClient:
                onUpdateClient && handleUpdateClient,
            onOrgIntegrationBulkUpdateEntity:
                onUpdateEntity && handleUpdateEntity,
            selectedOrgIntegrationAccountCount: selectedCount,
            eligibleOrgIntegrationReminderCount: eligibleAccountCount,
            eligibleOrgIntegrationClientCount: eligibleClientCount,
            eligibleOrgIntegrationEntityCount: eligibleEntityCount,
        });
    }, [
        openCommands,
        handleSendEmail,
        handleClearSelection,
        onUpdateClient,
        handleUpdateClient,
        onUpdateEntity,
        handleUpdateEntity,
        selectedCount,
        eligibleAccountCount,
        eligibleClientCount,
        eligibleEntityCount,
    ]);

    // Mirrored through a ref so the effect's deps stay stable while the handler
    // changes on every selection change.
    const handleOpenRef = useRef(handleOpenBulkActions);
    handleOpenRef.current = handleOpenBulkActions;

    const hasSelection = selectedCount > 0;

    useEffect(() => {
        if (hasSelection) {
            registerOpenOverride(() => handleOpenRef.current());
            return () => unregisterOpenOverride();
        }
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);
}
